import React, { useState } from 'react';
import { X, Download, Film, Layers, Folder, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

// Project Templates
const PROJECT_TEMPLATES = [
  {
    id: 'samba',
    name: 'Samba Batucada',
    description: '8-track setup for full samba bateria',
    tracks: ['Surdo 1', 'Surdo 2', 'Caixa', 'Repinique', 'Tamborim', 'Agogô', 'Ganzá', 'Cuíca'],
    bpm: 120,
    icon: '🥁'
  },
  {
    id: 'bossa',
    name: 'Bossa Nova',
    description: '4-track intimate bossa setup',
    tracks: ['Guitar', 'Voice', 'Bass', 'Percussion'],
    bpm: 140,
    icon: '🎸'
  },
  {
    id: 'vocal',
    name: 'Vocal Harmony',
    description: '4-track a cappella arrangement',
    tracks: ['Lead', 'Harmony 1', 'Harmony 2', 'Bass'],
    bpm: 100,
    icon: '🎤'
  },
  {
    id: 'band',
    name: 'Full Band',
    description: '6-track band recording',
    tracks: ['Drums', 'Bass', 'Guitar', 'Keys', 'Lead Vocal', 'Backing Vocal'],
    bpm: 120,
    icon: '🎹'
  },
  {
    id: 'practice',
    name: 'Practice Session',
    description: '2-track for learning along',
    tracks: ['Reference Track', 'My Recording'],
    bpm: 100,
    icon: '📚'
  },
  {
    id: 'duet',
    name: 'Duet',
    description: '2-track for collaboration',
    tracks: ['Part 1', 'Part 2'],
    bpm: 120,
    icon: '👥'
  }
];

export default function ProFeatures({ 
  isOpen, 
  onClose, 
  tracks = [], 
  projectName = 'Project',
  onApplyTemplate,
  onExportStems,
  onExport4K
}) {
  const [activeTab, setActiveTab] = useState('export'); // export, templates
  const [exportProgress, setExportProgress] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1080p');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Export stems (individual tracks)
  const handleStemExport = async () => {
    if (tracks.length === 0) {
      toast.error('No tracks to export');
      return;
    }

    setIsExporting(true);
    setExportProgress('Preparing stems...');

    try {
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        setExportProgress(`Exporting track ${i + 1} of ${tracks.length}...`);
        
        // Create download link
        const ext = track.blob.type.includes('mp4') ? 'mp4' : 'webm';
        const filename = `${projectName}_Track${i + 1}_${track.name || 'audio'}.${ext}`;
        
        const url = URL.createObjectURL(track.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        // Small delay between downloads
        await new Promise(r => setTimeout(r, 500));
      }
      
      toast.success(`Exported ${tracks.length} stems!`);
      if (onExportStems) onExportStems();
    } catch (err) {
      console.error('Stem export error:', err);
      toast.error('Export failed');
    }

    setIsExporting(false);
    setExportProgress('');
  };

  // Export in 4K (or selected resolution)
  const handleHighResExport = async () => {
    if (tracks.length === 0) {
      toast.error('No tracks to export');
      return;
    }

    setIsExporting(true);
    setExportProgress(`Rendering ${selectedResolution} video...`);

    // Simulate rendering process
    // In a real implementation, this would use a canvas to composite all video tracks
    // and encode to the selected resolution using MediaRecorder or FFmpeg.js
    
    try {
      const resolutions = {
        '720p': { width: 1280, height: 720 },
        '1080p': { width: 1920, height: 1080 },
        '4K': { width: 3840, height: 2160 }
      };

      const res = resolutions[selectedResolution];
      
      // For now, we'll export the first video track at original quality
      // A full implementation would composite all tracks
      const videoTrack = tracks.find(t => t.blob.type.includes('video'));
      
      if (!videoTrack) {
        // If no video, just combine audio
        setExportProgress('Combining audio tracks...');
        await new Promise(r => setTimeout(r, 1000));
        
        // Create combined audio (simplified - just export first track)
        const audioTrack = tracks[0];
        const url = URL.createObjectURL(audioTrack.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${projectName}_${selectedResolution}_audio.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success('Audio exported!');
      } else {
        setExportProgress(`Rendering at ${res.width}x${res.height}...`);
        await new Promise(r => setTimeout(r, 2000));
        
        // Export the video
        const url = URL.createObjectURL(videoTrack.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${projectName}_${selectedResolution}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(`${selectedResolution} video exported!`);
      }
      
      if (onExport4K) onExport4K();
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Export failed');
    }

    setIsExporting(false);
    setExportProgress('');
  };

  // Apply template
  const handleApplyTemplate = (template) => {
    if (onApplyTemplate) {
      onApplyTemplate(template);
      toast.success(`Applied "${template.name}" template`);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-stone-700">
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-amber-500" />
          <h2 className="text-xl font-bold">Pro Features</h2>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-stone-700 rounded-lg">
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-stone-700">
        <button
          onClick={() => setActiveTab('export')}
          className={`flex-1 py-3 font-bold flex items-center justify-center gap-2 ${
            activeTab === 'export' ? 'bg-amber-600 text-black' : 'bg-stone-800'
          }`}
        >
          <Download className="w-5 h-5" />
          Export Options
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`flex-1 py-3 font-bold flex items-center justify-center gap-2 ${
            activeTab === 'templates' ? 'bg-amber-600 text-black' : 'bg-stone-800'
          }`}
        >
          <Folder className="w-5 h-5" />
          Project Templates
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Export Options */}
        {activeTab === 'export' && (
          <div className="space-y-6 max-w-xl mx-auto">
            {/* 4K / High-Res Export */}
            <div className="bg-stone-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Film className="w-8 h-8 text-cyan-400" />
                <div>
                  <h3 className="text-lg font-bold">High-Resolution Export</h3>
                  <p className="text-sm text-stone-400">Export your project in up to 4K quality</p>
                </div>
              </div>

              <div className="flex gap-2 mb-4">
                {['720p', '1080p', '4K'].map(res => (
                  <button
                    key={res}
                    onClick={() => setSelectedResolution(res)}
                    className={`flex-1 py-3 rounded-lg font-bold ${
                      selectedResolution === res 
                        ? 'bg-cyan-500 text-black' 
                        : 'bg-stone-700'
                    }`}
                  >
                    {res}
                    {res === '4K' && <span className="ml-1 text-xs">✨</span>}
                  </button>
                ))}
              </div>

              <button
                onClick={handleHighResExport}
                disabled={isExporting || tracks.length === 0}
                className="w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:from-stone-700 disabled:to-stone-700 rounded-xl font-bold text-lg disabled:opacity-50"
              >
                {isExporting ? exportProgress : `Export ${selectedResolution} Video`}
              </button>
            </div>

            {/* Stem Export */}
            <div className="bg-stone-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Layers className="w-8 h-8 text-purple-400" />
                <div>
                  <h3 className="text-lg font-bold">Stem Export</h3>
                  <p className="text-sm text-stone-400">Download each track as a separate file</p>
                </div>
              </div>

              <div className="bg-stone-700 rounded-lg p-3 mb-4">
                <p className="text-sm">
                  <span className="font-bold text-purple-400">{tracks.length}</span> track(s) will be exported individually
                </p>
                {tracks.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {tracks.slice(0, 6).map((t, i) => (
                      <span key={i} className="px-2 py-1 bg-stone-600 rounded text-xs">
                        Track {i + 1}
                      </span>
                    ))}
                    {tracks.length > 6 && (
                      <span className="px-2 py-1 bg-stone-600 rounded text-xs">
                        +{tracks.length - 6} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={handleStemExport}
                disabled={isExporting || tracks.length === 0}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-stone-700 disabled:to-stone-700 rounded-xl font-bold text-lg disabled:opacity-50"
              >
                {isExporting ? exportProgress : 'Download All Stems'}
              </button>
            </div>
          </div>
        )}

        {/* Project Templates */}
        {activeTab === 'templates' && (
          <div className="space-y-4 max-w-2xl mx-auto">
            <p className="text-stone-400 text-center mb-6">
              Start with a pre-configured project setup for your recording style
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              {PROJECT_TEMPLATES.map(template => (
                <div
                  key={template.id}
                  className={`bg-stone-800 rounded-xl p-4 cursor-pointer transition-all ${
                    selectedTemplate?.id === template.id 
                      ? 'ring-2 ring-amber-500' 
                      : 'hover:bg-stone-750'
                  }`}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{template.icon}</span>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{template.name}</h3>
                      <p className="text-sm text-stone-400">{template.description}</p>
                      <p className="text-xs text-amber-400 mt-1">{template.bpm} BPM</p>
                    </div>
                    {selectedTemplate?.id === template.id && (
                      <Check className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                  
                  <div className="mt-3 flex flex-wrap gap-1">
                    {template.tracks.map((track, i) => (
                      <span key={i} className="px-2 py-1 bg-stone-700 rounded text-xs">
                        {track}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {selectedTemplate && (
              <div className="fixed bottom-0 left-0 right-0 p-4 bg-stone-900 border-t border-stone-700">
                <button
                  onClick={() => handleApplyTemplate(selectedTemplate)}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 rounded-xl font-bold text-lg text-black"
                >
                  Apply "{selectedTemplate.name}" Template
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
